import { useCallback, useEffect, useRef, useState } from 'react';

const defaultPageSize = 15;

type LazyLoadParamValue = string | number | boolean;
type ParamsType = Record<string, LazyLoadParamValue>;
type GetNextPageParamsType = (
  items: any[],
) => Record<string, LazyLoadParamValue>;

export function useLazyLoad(
  useLazyGet: () => any,
  params: ParamsType = {},
  getNextPageParams?: GetNextPageParamsType,
  skipLoad?: boolean,
) {
  const skipLoadRef = useRef(!!skipLoad);
  useEffect(() => {
    skipLoadRef.current = !!skipLoad;
  }, [skipLoad]);

  const footerRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const isFetchingRef = useRef(false);
  const paramsRef = useRef<ParamsType | undefined>(undefined);
  const getNextPageParamsRef = useRef<GetNextPageParamsType | undefined>(
    undefined,
  );
  const pageNumber = useRef(0);
  const isEndReached = useRef(false);
  const isBlockedByErrorRef = useRef(false);
  const failedRequestRef = useRef<{
    requestParams: Record<string, LazyLoadParamValue>;
    pageNumber: number;
    pageSize: number;
  } | null>(null);
  const [items, setItems] = useState<any[] | null>(null);
  const itemsRef = useRef<any[]>([]);

  const [trigger, { isFetching }] = useLazyGet();
  const fetchNextPageRef = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  useEffect(() => {
    getNextPageParamsRef.current = getNextPageParams;
  }, [getNextPageParams]);

  useEffect(() => {
    itemsRef.current = items ?? [];
  }, [items]);

  const footerRefCallback = useCallback((el: HTMLElement | null) => {
    if (footerRef.current && observerRef.current) {
      observerRef.current.unobserve(footerRef.current);
    }
    footerRef.current = el;
    if (el && observerRef.current) {
      observerRef.current.observe(el);
    }
  }, []);

  const [error, setError] = useState<unknown>(null);

  const fetchNextPage = useCallback(async () => {
    if (skipLoadRef.current) return;
    if (
      isFetchingRef.current ||
      isEndReached.current ||
      isBlockedByErrorRef.current
    ) {
      return;
    }
    isFetchingRef.current = true;

    const currentItems = itemsRef.current;
    const nextPageNumber = pageNumber.current + 1;

    const nextPageParams = getNextPageParamsRef.current
      ? getNextPageParamsRef.current(currentItems)
      : { page: nextPageNumber };

    setError(null);
    const pageSize = Number(paramsRef.current?.pageSize ?? defaultPageSize);
    const requestParams = {
      ...paramsRef.current,
      ...nextPageParams,
      pageSize,
    };

    try {
      const result = await trigger(requestParams, true).unwrap();
      pageNumber.current = nextPageNumber;
      isBlockedByErrorRef.current = false;
      failedRequestRef.current = null;
      if (result?.length < pageSize) isEndReached.current = true;
      setItems((prev) => [...(prev ?? []), ...(result ?? [])]);
    } catch (e) {
      isBlockedByErrorRef.current = true;
      failedRequestRef.current = {
        requestParams,
        pageNumber: nextPageNumber,
        pageSize: Number(pageSize),
      };
      setError(e);
      console.error('[useLazyLoad] fetchNextPage failed:', e);
    } finally {
      isFetchingRef.current = false;
    }
  }, [trigger]);

  const retryLastRequest = useCallback(async () => {
    if (skipLoadRef.current) return;
    const failedRequest = failedRequestRef.current;
    if (!failedRequest || isFetchingRef.current) return;
    isFetchingRef.current = true;
    setError(null);

    try {
      const result = await trigger(failedRequest.requestParams, true).unwrap();
      pageNumber.current = failedRequest.pageNumber;
      isBlockedByErrorRef.current = false;
      failedRequestRef.current = null;
      if (result?.length < failedRequest.pageSize) isEndReached.current = true;
      setItems((prev) => [...(prev ?? []), ...(result ?? [])]);
    } catch (e) {
      isBlockedByErrorRef.current = true;
      setError(e);
      console.error('[useLazyLoad] retryLastRequest failed:', e);
    } finally {
      isFetchingRef.current = false;
    }
  }, [trigger]);

  useEffect(() => {
    fetchNextPageRef.current = fetchNextPage;
  }, [fetchNextPage]);

  const didInitialFetchRef = useRef(false);

  useEffect(() => {
    if (skipLoad) return;
    if (didInitialFetchRef.current) return;
    didInitialFetchRef.current = true;
    void fetchNextPage();
  }, [fetchNextPage, skipLoad]);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          void fetchNextPageRef.current?.();
        }
      },
      {
        root: null,
        threshold: 0,
      },
    );

    const el = footerRef.current;
    if (el) observerRef.current.observe(el);

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, []);

  const refetchAll = useCallback(async () => {
    if (skipLoadRef.current || isFetchingRef.current) return;
    isFetchingRef.current = true;
    isEndReached.current = false;
    isBlockedByErrorRef.current = false;
    failedRequestRef.current = null;
    setError(null);
    const newItems: any[] = [];
    const pageSize = paramsRef.current?.pageSize ?? defaultPageSize;

    try {
      for (let i = 1; i <= pageNumber.current; i++) {
        const nextPageParams = getNextPageParamsRef.current
          ? getNextPageParamsRef.current(newItems)
          : { page: i };

        const result = await trigger({
          ...paramsRef.current,
          ...nextPageParams,
          pageSize,
        }).unwrap();
        newItems.push(...(result ?? []));
      }
      setItems(newItems);
    } catch (e) {
      setError(e);
      console.error('[useLazyLoad] refetchAll failed:', e);
    } finally {
      isFetchingRef.current = false;
    }
  }, [trigger]);

  const resetAll = useCallback(
    (refetch = false) => {
      pageNumber.current = 0;
      setItems([]);
      setError(null);
      isEndReached.current = false;
      isBlockedByErrorRef.current = false;
      failedRequestRef.current = null;

      if (refetch) {
        void fetchNextPage();
      }
    },
    [fetchNextPage],
  );

  return {
    items,
    footerRef: footerRefCallback,
    refetchAll,
    resetAll,
    retryLastRequest,
    isFetching,
    error,
  };
}
